import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Rocket Van Express service areas with coordinates
const SERVICE_AREAS = [
  { name: 'Stellenbosch', lat: -33.9321, lng: 18.8602, type: 'primary' },
  { name: 'Somerset West', lat: -34.0781, lng: 18.8431, type: 'primary' },
  { name: 'Cape Town CBD', lat: -33.9249, lng: 18.4241, type: 'primary' },
  { name: 'Paarl', lat: -33.7141, lng: 18.9644, type: 'secondary' },
  { name: 'Bellville', lat: -33.9017, lng: 18.6283, type: 'secondary' },
  { name: 'Durbanville', lat: -33.8313, lng: 18.6476, type: 'secondary' },
  { name: 'Gordons Bay', lat: -34.1616, lng: 18.8673, type: 'secondary' },
  { name: 'Strand', lat: -34.1061, lng: 18.8292, type: 'secondary' },
  { name: 'Franschhoek', lat: -33.9135, lng: 19.1162, type: 'secondary' },
  { name: 'Wellington', lat: -33.6439, lng: 19.0102, type: 'secondary' },
];

// Center point for the map (roughly center of service area)
const MAP_CENTER = { lat: -33.95, lng: 18.75 };
const MAP_ZOOM = 10;

export const GoogleMap = ({ 
  height = '400px', 
  showMarkers = true,
  center = MAP_CENTER,
  zoom = MAP_ZOOM,
  singleLocation = null // For area-specific pages
}) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current || !window.google) return;

    try {
      const mapCenter = singleLocation 
        ? { lat: singleLocation.lat, lng: singleLocation.lng }
        : center;
      
      const mapZoom = singleLocation ? 13 : zoom;

      const map = new window.google.maps.Map(mapRef.current, {
        center: mapCenter,
        zoom: mapZoom,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      if (showMarkers) {
        const locations = singleLocation ? [singleLocation] : SERVICE_AREAS;
        
        locations.forEach((area) => {
          const marker = new window.google.maps.Marker({
            position: { lat: area.lat, lng: area.lng },
            map: map,
            title: area.name,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: area.type === 'primary' || singleLocation ? 12 : 8,
              fillColor: area.type === 'primary' || singleLocation ? '#fb6402' : '#039fff',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });

          // Info window for each marker
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; font-family: Arial, sans-serif;">
                <h3 style="margin: 0 0 4px 0; color: #001A47; font-size: 14px;">${area.name}</h3>
                <p style="margin: 0; color: #666; font-size: 12px;">Rocket Van Express Service Area</p>
              </div>
            `,
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });
        });
      }

      setMapLoaded(true);
    } catch (err) {

      setError('Failed to initialize map');
    }
  };

  if (error) {
    return (
      <div 
        style={{ height, width: '100%' }}
        className="bg-gray-100 rounded-lg flex items-center justify-center"
      >
        <div className="text-center text-gray-500">
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-1">Map unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden" style={{ height, width: '100%' }}>
      {!mapLoaded && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#039fff] mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

// Simple static map for when dynamic isn't needed
export const StaticMap = ({ location, height = '300px' }) => {
  if (!GOOGLE_MAPS_API_KEY || !location) {
    return (
      <div 
        style={{ height }}
        className="bg-gray-100 rounded-lg flex items-center justify-center"
      >
        <p className="text-sm text-gray-500">Map preview unavailable</p>
      </div>
    );
  }

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${location.lat},${location.lng}&zoom=13&size=600x300&scale=2&markers=color:0xfb6402|${location.lat},${location.lng}&key=${GOOGLE_MAPS_API_KEY}`;

  return (
    <div className="rounded-lg overflow-hidden" style={{ height }}>
      <img 
        src={staticMapUrl} 
        alt={`Map of ${location.name || 'location'}`}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

export default GoogleMap;
